// A call's recording, shared by the QA report (a graded call) and the audit calls page (the call being audited).
// Fetches the combined recording, or asks the server to prepare it and polls the job.
import { toRaw } from 'vue';
import { getJobStatus } from '@/services/api/jobsApi';
import { getCallAudio, prepareCallAudio } from '@/services/api/voiceApi';

export default {
  data() {
    return {
      humanRecordingUrl: '',
      humanRecordingLoading: false,
      // The call whose recording is loading or loaded.
      recordingCall: null,
      recordingError: '',
      audioPollTimer: null,
    };
  },
  beforeUnmount() {
    this.resetRecording();
  },
  methods: {
    // Calls reach here both as reactive proxies (from data) and as plain objects, so compare the objects underneath.
    isRecordingFor(call) {
      return !!call && toRaw(this.recordingCall) === toRaw(call);
    },
    resetRecording() {
      if (this.audioPollTimer) clearTimeout(this.audioPollTimer);
      this.audioPollTimer = null;
      if (this.humanRecordingUrl) URL.revokeObjectURL(this.humanRecordingUrl);
      this.humanRecordingUrl = '';
      this.humanRecordingLoading = false;
      this.recordingCall = null;
      this.recordingError = '';
    },
    async loadRecording(call) {
      this.resetRecording();
      this.recordingCall = call;
      this.humanRecordingLoading = true;
      try {
        const uuid = call.call_log_uuid;
        if (call.call_audio_url) {
          this.humanRecordingUrl = URL.createObjectURL(await getCallAudio(uuid));
          return;
        }
        const result = await prepareCallAudio(uuid);
        if (result.call_audio_url) {
          this.humanRecordingUrl = URL.createObjectURL(await getCallAudio(uuid));
          return;
        }
        if (result.job_id) {
          await this.pollJobUntilReady(uuid, result.job_id);
          return;
        }
        throw new Error('No recording is available.');
      } catch (e) {
        this.recordingError = e?.response?.data?.detail || e.message || 'Could not load the recording.';
      } finally {
        if (this.isRecordingFor(call)) this.humanRecordingLoading = false;
      }
    },
    pollJobUntilReady(uuid, jobId) {
      const expectedCall = this.recordingCall;
      const poll = async (resolve, reject) => {
        // Opening another call mid-poll ends the poll; the recording is not loaded.
        if (this.recordingCall !== expectedCall) return resolve();
        try {
          const data = await getJobStatus(jobId);
          if (data.status === 'completed') {
            if (this.recordingCall !== expectedCall) return resolve();
            this.humanRecordingUrl = URL.createObjectURL(await getCallAudio(uuid));
            resolve();
          } else if (data.status === 'failed') {
            reject(new Error(data.error_message || 'Failed to prepare recording'));
          } else {
            this.audioPollTimer = setTimeout(() => poll(resolve, reject), 2000);
          }
        } catch (e) {
          reject(e);
        }
      };
      return new Promise(poll);
    },
  },
};
